import { clerkMiddleware } from "@clerk/tanstack-react-start/server";
import { createCsrfMiddleware, createMiddleware, createStart } from "@tanstack/react-start";

import {
  captureServerGlitchTipException,
  setServerGlitchTipContext,
  setServerGlitchTipTag,
  tagServerResponse,
  withServerGlitchTipRequestScope,
} from "./lib/glitchtip-server";

const requestMonitoringMiddleware = createMiddleware().server(async ({ next, request }) => {
  const startedAt = Date.now();

  return await withServerGlitchTipRequestScope(request, async () => {
    try {
      const response = await next();
      if (response instanceof Response) {
        tagServerResponse(response.status, Date.now() - startedAt);
      }

      return response;
    } catch (error) {
      const durationMs = Date.now() - startedAt;
      setServerGlitchTipTag("request.failed", true);
      setServerGlitchTipContext("request_timing", { durationMs });
      captureServerGlitchTipException(error, {
        tags: {
          kind: "request",
        },
        contexts: {
          request_timing: {
            durationMs,
          },
        },
      });
      throw error;
    }
  });
});

const functionMonitoringMiddleware = createMiddleware({ type: "function" }).server(
  async ({ next }) => {
    const startedAt = Date.now();
    setServerGlitchTipTag("request.kind", "server_function");

    try {
      const result = await next();
      setServerGlitchTipContext("server_function", {
        durationMs: Date.now() - startedAt,
      });
      setServerGlitchTipTag("request.kind", "server_function");
      return result;
    } catch (error) {
      const durationMs = Date.now() - startedAt;
      setServerGlitchTipContext("server_function", {
        durationMs,
      });
      captureServerGlitchTipException(error, {
        tags: {
          kind: "server_function",
        },
        contexts: {
          server_function: {
            durationMs,
          },
        },
      });
      throw error;
    }
  },
);

export const startInstance = createStart(() => {
  return {
    // Start installs no CSRF middleware once a project declares its own start instance; server
    // functions get it here, ahead of Clerk, as its changelog asks.
    requestMiddleware: [
      createCsrfMiddleware({ filter: (context) => context.handlerType === "serverFn" }),
      clerkMiddleware(),
      requestMonitoringMiddleware,
    ],
    functionMiddleware: [functionMonitoringMiddleware],
  };
});
